import type { AppliedConfiguration, SwadeModConfigurationInterface, SwadeModificationInterface } from '@/types/items';
import type { SwadePowerContainerInterface } from '@/types/powers';
import type { WorldInterface } from '@/types/worlds';
import { authoredTargetMode, isOfferedOption } from '@/appdata/container-targets';
import { resolvedModificationUuid } from '@/appdata/applied-configuration';
import { carriedEntries, resolvedByRecord } from '@/appdata/container-carry';
import { deepCopyObject } from '@/appdata/globals';
import { catalogChoiceSets, directGrants, grantMultiplier, isLegacyPowerContainer, isTargetGrant, levelCount } from '@/appdata/power-levels';
import { isSkillRankEffect, skillRankAmount } from '@/appdata/skill-rank-effects';

/**
 * ONE row shape for both container shapes (spec 2026-09-21 §4.7, §5.5, ruling 19). A legacy
 * container's rows come from its `configuration.modifiers` and copy list; a schema-2 container's from
 * its choice records and the catalog's fixed grants. No template or component branches on the shape;
 * these two selectors and PowerContainer's PHP accessors are the only places that do.
 *
 * One deliberate difference from the PHP twin: a legacy grant row's value here is the copy's OWN
 * applied value (what the card always showed), while PowerContainer::getGrantRows() prints the level
 * count for PDF parity (ruling 15).
 */

export interface PickedPowerOption {
    label: string | null;
    operator: 'add' | 'subtract';
    /**
     * Never negative: the operator carries the sign of the whole. A repeat is the multiplied value; the cost
     * the set's records carry on the power's own path is added (carriedPowerPoints, owner 2026-09-27).
     */
    value: number;
    legacy: boolean;
    /** Schema 2 only. */
    set: SwadeModificationInterface | null;
    rung: Partial<SwadeModificationInterface> | null;
    record: AppliedConfiguration | null;
    /** How many times the option is taken (a one-rung set's record value over the rung's value). */
    count: number;
    /**
     * The containers the player picked for the set's blank-element container effects, in record order
     * (owner, 2026-09-28): Alternate Trait's Occult. Empty when the set asked for no target — and for
     * every legacy row. PHP twin: the `targets` key of PowerContainer::getPickedOptions().
     */
    targets: string[];
    /**
     * The row's position in `configuration.modifiers` — legacy rows only; null for schema 2, whose
     * removal goes by record instead (SuperPowerCard.vue's onOptionRemoved). Card-only: the PHP twin
     * (PowerContainer::getGrantRows()) has no such field — a legacy row is never sent to PHP.
     */
    index: number | null;
}

export interface GrantRow {
    label: string | null;
    /** The configuration path behind the row, so the card can format a copy by its target kind. */
    path: string | null;
    /** A count of levels or steps; a skill rank pick's change is signed, as a subtract can be (owner, 2026-10-08). */
    value: number;
    legacy: boolean;
    /** Schema 2: the record behind a picked grant; null for a fixed catalog grant or a legacy copy. */
    record: AppliedConfiguration | null;
    /** Schema 2: the catalog grant the row belongs to, so the card can group rows under its picker. */
    grant: SwadeModificationInterface | null;
}

const CONTAINER_POWERS = 'container.powers';

/** The TS twin of the PHP ModificationLabel::flatten(): a string or number as is, a chip list joined, else null. */
export function labelText(label: unknown): string | null {
    if (typeof label === 'string') return label;
    if (typeof label === 'number' && Number.isFinite(label)) return String(label);
    if (!Array.isArray(label)) return null;
    const texts = label
        .map((chip) => (chip && typeof chip === 'object' ? (chip as { text?: unknown }).text : chip))
        .filter((text): text is string => typeof text === 'string' && text.trim() !== '')
        .map((text) => text.trim());
    return texts.length === 0 ? null : texts.join(', ');
}

const numeric = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
};

// Case- and accent-insensitive, with numeric runs compared as numbers: "Armor Piercing" sits beside
// "armor piercing", and "Rung 10" follows "Rung 9" rather than preceding it.
const collator = new Intl.Collator(undefined, { sensitivity: 'base', numeric: true });

/**
 * The order a modifier combo box lists its options in. Both cards' selectors sort through this, each
 * passing the very string its own ComboBox renders through `text-field` — the offered set's
 * `configuration.label` on a super power, a legacy catalog modifier's `label` on a core power — so
 * what the list is sorted by is what the player reads.
 */
export const compareModifierLabels = (a: string | null | undefined, b: string | null | undefined): number =>
    collator.compare(a ?? '', b ?? '');

/**
 * The offered sets a power can pick from: its own catalog's and the world's — a universal modifier
 * reaches a power this way — once each by uuid, in label order.
 *
 * Collected catalog-first so the power's own set wins the uuid de-duplication, THEN sorted: the
 * precedence rule and the presentation order are separate, and sorting after the `seen` filter
 * leaves the former untouched. Order is presentation only; pickedPowerOptions below reads this list
 * by `.find()`.
 */
export function offeredPowerOptions(
    power: SwadePowerContainerInterface | null | undefined,
    world: WorldInterface | null | undefined,
): SwadeModificationInterface[] {
    const seen = new Set<string>();
    const out: SwadeModificationInterface[] = [];
    for (const list of [power?.selection?.modifications, world?.modifications]) {
        if (!Array.isArray(list)) continue;
        for (const mod of list) {
            if (!mod?.configuration || !isOfferedOption(mod.configuration) || mod.configuration.path !== 'character.choiceSet') continue;
            const key = mod.uuid ?? '';
            if (key === '' || seen.has(key)) continue;
            seen.add(key);
            out.push(mod);
        }
    }
    return out.sort((a, b) => compareModifierLabels(a.configuration.label, b.configuration.label));
}

/** The choice records keyed by the container, in record order. */
export function ownPowerRecords(character: any, power: SwadePowerContainerInterface | null | undefined): AppliedConfiguration[] {
    const records = character?.additionalData?.choices;
    if (!Array.isArray(records) || typeof power?.uuid !== 'string') return [];
    return records.filter((record: AppliedConfiguration) => record?.uuid === power.uuid);
}

/** The key of a rung's carried effect the player targets: the set uuid, `#carry#`, the entry's index in carriedEntries (spec 2026-09-22 v2.2 §3.2). */
export const carriedEffectKey = (setUuid: string, index: number): string => `${setUuid}#carry#${index}`;
export const carriedEffectKeyPrefix = (setUuid: string): string => `${setUuid}#carry#`;

/**
 * A picked rung's record-resolved effects as synthetic modifications — one per carried entry that
 * names a target the player picks (`resolvedByRecord`), keyed so the container-target resolver, the
 * pencil and the records all pair on the same uuid. Configurations are copies.
 */
export function rungEffectModifications(
    set: SwadeModificationInterface | null | undefined,
    rung: Partial<SwadeModificationInterface> | null | undefined,
): SwadeModificationInterface[] {
    if (typeof set?.uuid !== 'string' || set.uuid.length === 0) return [];
    const setUuid = set.uuid;
    return carriedEntries(rung?.configuration as SwadeModConfigurationInterface | undefined)
        .map((entry, index) => ({ entry, index }))
        .filter(({ entry }) => resolvedByRecord(entry))
        .map(({ entry, index }) => ({ uuid: carriedEffectKey(setUuid, index), version: 1, configuration: deepCopyObject(entry) } as SwadeModificationInterface));
}

/**
 * A picked rung's choice-set effects as the prompts the player answers (ruling 48): the authored set
 * itself under the carried-effect key, asking under its own label, else the rung's, else the set's,
 * and — when the rung is repeated — multi-select capped at the points invested. The pick and the
 * card's header pencil both shape the prompt here, so a re-choice asks exactly what the pick asked.
 */
export function rungChoiceSetPrompts(
    set: SwadeModificationInterface | null | undefined,
    rung: Partial<SwadeModificationInterface> | null | undefined,
    repeats: number = 1,
): SwadeModificationInterface[] {
    return rungEffectModifications(set, rung)
        .filter((mod) => mod.configuration.path === 'character.choiceSet')
        .map((mod) => {
            mod.configuration.label = mod.configuration.label ?? rung?.configuration?.label ?? set?.configuration?.label ?? null;
            return repeatedPrompt(mod, repeats);
        });
}

/**
 * The ONE shaping of a choice set the player answers `repeats` times over: multi-select capped at the
 * count when it is more than one, as authored otherwise (ruling 48 for a repeated rung's effect; owner,
 * 2026-09-25, for a scaling power's own set). Mutates the COPY it is handed and returns it.
 */
const repeatedPrompt = (mod: SwadeModificationInterface, repeats: number): SwadeModificationInterface => {
    if (repeats > 1) {
        mod.multiple = true;
        mod.configuration.maxPicks = repeats;
    }
    return mod;
};

/**
 * The catalog's own (non-offered) choice sets as the prompts the player answers (owner, 2026-09-25): a
 * scaling power's set allows one pick per level — Heightened Senses buys one sense per point — so each
 * set is a COPY shaped by the level count, exactly as a repeated rung's effect is. The add path and the
 * card's header pencil both ask through here, so a re-choice asks what the level now allows.
 */
export function catalogChoiceSetPrompts(power: SwadePowerContainerInterface | null | undefined): SwadeModificationInterface[] {
    const repeats = grantMultiplier(power);
    return catalogChoiceSets(power).map((set) => repeatedPrompt(deepCopyObject(set), repeats));
}

/**
 * Everything the power's header pencil re-asks (owner, 2026-09-24): the catalog's own choice sets
 * (Reach) and each picked rung's choice-set effects (Additional Sense), shaped as at pick time. The
 * SETS ALONE — never the catalog's grants: a blank grant handed to the resolver would prompt through a
 * dynamic resolver. An offered set is a modifier the card picks and is not re-asked here.
 */
export function powerChoiceSources(
    character: any,
    power: SwadePowerContainerInterface | null | undefined,
    world: WorldInterface | null | undefined,
): SwadeModificationInterface[] {
    const picked = pickedPowerOptions(character, power, world)
        .flatMap((row) => (row.set && row.rung ? rungChoiceSetPrompts(row.set, row.rung, row.count) : []));
    return [...catalogChoiceSetPrompts(power), ...picked];
}

/** Every record a set owns on a power (spec v2.2 §3.4): its rung record and its target records, whoever wrote them. */
export function recordsOfSet(records: AppliedConfiguration[] | undefined, setUuid: string): AppliedConfiguration[] {
    if (!Array.isArray(records) || setUuid === '') return [];
    const prefix = carriedEffectKeyPrefix(setUuid);
    return records.filter((record) => {
        const key = resolvedModificationUuid(record) ?? '';
        return key === setUuid || key.startsWith(prefix);
    });
}

const rungsOf = (set: SwadeModificationInterface | null | undefined): Partial<SwadeModificationInterface>[] =>
    Array.isArray(set?.configuration?.element) ? set!.configuration.element as Partial<SwadeModificationInterface>[] : [];

/**
 * The rung a record holds and how many times it is taken. A one-rung set's record may hold a
 * multiple of the rung (a repeat under maxPicks); a several-rung set's record matches exactly one
 * rung by operator and value, or none.
 */
export function matchRung(
    set: SwadeModificationInterface,
    configuration: SwadeModConfigurationInterface,
): { rung: Partial<SwadeModificationInterface> | null; count: number } {
    const rungs = rungsOf(set);
    const operator = configuration.operator === 'subtract' ? 'subtract' : 'add';
    const value = Math.abs(numeric(configuration.value) ?? 0);
    if (rungs.length === 1) {
        const rungValue = Math.abs(numeric(rungs[0]?.configuration?.value) ?? 0);
        const count = rungValue > 0 && value % rungValue === 0 ? value / rungValue : 1;
        return { rung: rungs[0], count: Math.max(1, count) };
    }
    const rung = rungs.find((option) =>
        (option.configuration?.operator === 'subtract' ? 'subtract' : 'add') === operator
        && Math.abs(numeric(option.configuration?.value) ?? 0) === value) ?? null;
    return { rung, count: 1 };
}

/**
 * How many times a rung record's rung was taken (ruling 45, 2026-09-23): the count stored on the
 * record when it was picked, else — for a record written before the field existed — the set's rung
 * against the record's value (matchRung), else one. The ONE derivation: the card's repeat select and
 * the carry sync's scaling both read it.
 */
export function recordRepeatCount(record: AppliedConfiguration | null | undefined, set: SwadeModificationInterface | null | undefined): number {
    const stored = record?.count;
    if (typeof stored === 'number' && Number.isInteger(stored) && stored >= 1) return stored;
    return set && record?.configuration ? matchRung(set, record.configuration).count : 1;
}

/** The cost row's entry: container.powers on powerPoints — a rung record, or an entry a record carries. */
const isPowerPointsEntry = (configuration: SwadeModConfigurationInterface | null | undefined): boolean =>
    configuration?.path === CONTAINER_POWERS && configuration?.detail === 'powerPoints';

/** A cost entry's points with its operator's sign: `subtract` negative, anything else positive. */
const signedPowerPoints = (configuration: SwadeModConfigurationInterface | null | undefined): number =>
    (configuration?.operator === 'subtract' ? -1 : 1) * Math.abs(numeric(configuration?.value) ?? 0);

/**
 * The cost a set's records carry on the power's own path, signed (owner, 2026-09-27, Altered Form's Reach): the
 * rung record's own carried container.powers/powerPoints entries once per point taken — the record walk's
 * factor — and each choice or target record's (keyed set#carry#N) once — the third source's, and the record
 * walk's for a record that is no row. Ruling 49 lands every one of them on the power, so the total was already
 * right; the MODIFIER's row reads them too, and a chosen Reach 2 makes Reach a +2 modifier rather than a +1
 * row beside a point on the power. Keyed by the rung record, so an unpaired set (a re-imported catalog) still
 * costs its row. PHP twin: PowerContainer::carriedPowerPoints().
 */
const carriedPowerPoints = (records: AppliedConfiguration[], rung: AppliedConfiguration, count: number): number =>
    recordsOfSet(records, resolvedModificationUuid(rung) ?? '').reduce((sum, record) =>
        sum + carriedEntries(record.configuration)
            .filter(isPowerPointsEntry)
            .reduce((points, entry) => points + signedPowerPoints(entry) * (record === rung ? count : 1), 0), 0);

/**
 * The containers a set's records name as the player's picks (owner, 2026-09-28, Alternate Trait): each of
 * the set's records that resolved to specific containers, by its own label — the container's display name
 * captured at pick time, and the ONLY place the pick is written when the effect carries a blank value, as
 * Alternate Trait's does (nothing reaches the power's trappings or the picked skill's).
 *
 * Only a `specific` record counts. A choice-set answer writes a VALUE on the power itself (Additional
 * Sense's Smell: no element, mode `self`) and already shows in the trappings; an `allSpecified` list was
 * authored rather than picked. The rung record itself is `self` and so never names a target. Read off the
 * records alone, never the set, so Alternate Trait — a universal modifier no power's catalog carries —
 * still names its target. PHP twin: PowerContainer::pickedTargets().
 */
const pickedTargets = (records: AppliedConfiguration[], rung: AppliedConfiguration): string[] =>
    recordsOfSet(records, resolvedModificationUuid(rung) ?? '')
        .filter((record) => authoredTargetMode(record.configuration) === 'specific')
        .map((record) => labelText(record.configuration.label))
        .filter((label): label is string => label !== null && label !== '');

export function pickedPowerOptions(
    character: any,
    power: SwadePowerContainerInterface | null | undefined,
    world: WorldInterface | null | undefined,
): PickedPowerOption[] {
    if (isLegacyPowerContainer(power)) {
        const modifiers = Array.isArray(power?.configuration?.modifiers) ? power!.configuration.modifiers : [];
        // Mapped over the ORIGINAL array, keeping the real index, THEN filtered — not
        // `.filter(Boolean).map(...)` — so `index` survives a falsy (skipped) entry earlier in the
        // list. SuperPowerCard.vue's legacy removal filters configuration.modifiers by this index.
        return modifiers.map((modifier, index): PickedPowerOption | null => {
            if (!modifier) return null;
            const multiplier = numeric(modifier.multiplier) ?? 1;
            const points = Math.round((numeric(modifier.level?.powerPointModifier) ?? 0) * multiplier);
            return {
                label: labelText(modifier.label) ?? null,
                operator: points < 0 ? 'subtract' : 'add',
                value: Math.abs(points),
                legacy: true,
                set: null,
                rung: null,
                record: null,
                count: Math.max(1, multiplier),
                targets: [],
                index,
            };
        }).filter((row): row is PickedPowerOption => row !== null);
    }

    const sets = offeredPowerOptions(power, world);
    const records = ownPowerRecords(character, power);
    // A row is a RUNG record — the cost row on container.powers/powerPoints. A record of a choice a rung's
    // choice-set effect resolved to (Additional Sense: `trapping add Smell`, keyed set#carry#N) alters the
    // power the way Special Weapon's weapon record alters the weapon, and is a modifier row nowhere: the
    // sense already shows in the trappings (owner's ruling 2026-09-24) — but the cost such a record carries
    // on the power's path is the row's (carriedPowerPoints). PHP twin: PowerContainer::getPickedOptions().
    return records
        .filter((record) => isPowerPointsEntry(record.configuration))
        .map((record) => {
            const set = sets.find((candidate) => candidate.uuid === resolvedModificationUuid(record)) ?? null;
            const rung = set ? matchRung(set, record.configuration).rung : null;
            const count = recordRepeatCount(record, set);
            const total = signedPowerPoints(record.configuration) + carriedPowerPoints(records, record, count);
            return {
                label: labelText(record.configuration.label) ?? set?.configuration.label ?? null,
                // A zero keeps the record's own operator, as before; anything else follows the sign of the whole.
                operator: total === 0 ? (record.configuration.operator === 'subtract' ? 'subtract' : 'add') : (total < 0 ? 'subtract' : 'add'),
                value: Math.abs(total),
                legacy: false,
                set,
                rung,
                record,
                count,
                targets: pickedTargets(records, record),
                index: null,
            };
        });
}

export function grantRows(character: any, power: SwadePowerContainerInterface | null | undefined): GrantRow[] {
    if (isLegacyPowerContainer(power)) {
        const copies = Array.isArray(power?.configuration?.modifications) ? power!.configuration.modifications : [];
        return copies.filter(Boolean).map((copy) => ({
            label: labelText(copy.configuration?.label),
            path: copy.configuration?.path ?? null,
            value: numeric(copy.configuration?.value) ?? levelCount(power),
            legacy: true,
            record: null,
            grant: null,
        }));
    }

    const grants = directGrants(power);
    const rows: GrantRow[] = ownPowerRecords(character, power)
        .map((record) => ({ record, grant: grants.find((grant) => grant.uuid === resolvedModificationUuid(record)) ?? null }))
        // A skill rank pick changes the picked skill as a Super Skill pick does (owner, 2026-10-08), so the power's
        // own grant's is a row too, signed; a picked modifier's rank effect is its modifier row's target instead.
        .filter(({ record, grant }) => String(record.configuration?.path ?? '').startsWith('character.')
            || (grant !== null && isSkillRankEffect(record.configuration)))
        .map(({ record, grant }) => ({
            label: labelText(record.configuration.label),
            path: record.configuration.path ?? null,
            value: isSkillRankEffect(record.configuration)
                ? (skillRankAmount(record.configuration) ?? 0)
                : (numeric(record.configuration.value) ?? 0),
            legacy: false,
            record,
            grant,
        }));

    // Fixed catalog grants print the level count (ruling 15); a blank-element grant is picked, not fixed.
    const multiplier = grantMultiplier(power);
    for (const grant of grants) {
        if (isTargetGrant(grant.configuration)) continue;
        rows.push({ label: labelText(grant.configuration.label), path: grant.configuration.path ?? null, value: multiplier, legacy: false, record: null, grant });
    }
    return rows;
}
